import * as React from "react";
import { Icon, TidyMark } from "../components/Icon.js";

export function LoginScreen({ instance }: { instance: string | null }) {
  return (
    <div className="login-root">
      <div className="login-bg" aria-hidden>
        <div className="blob b1" />
        <div className="blob b2" />
        <div className="blob b3" />
        <svg className="grid" width="100%" height="100%" aria-hidden>
          <defs>
            <pattern id="g" width="32" height="32" patternUnits="userSpaceOnUse">
              <path d="M32 0H0v32" fill="none" stroke="rgba(13,27,42,0.04)" strokeWidth="1" />
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#g)" />
        </svg>
      </div>

      <header className="login-top">
        <div className="brand-lockup">
          <div className="brand-mark"><span className="dot" />Tidy<b>View</b></div>
        </div>
      </header>

      <main className="login-main">
        <div className="login-card fade-in">
          <div className="login-eyebrow">
            <span className="dot-pulse" /> Sign in
          </div>
          <h1 className="login-title">
            Welcome back.
            <br />
            <span className="muted">Let's see who's coming.</span>
          </h1>
          <p className="login-sub">
            TidyView is a flexible registrant dashboard for organisations using TidyHQ.
            Sign in with your TidyHQ account to get started — we never see your password.
          </p>

          <a className="oauth-btn" href="/auth/login">
            <span className="oauth-icon">
              <TidyMark size={22} color="white" />
            </span>
            <span className="oauth-label">Continue with TidyHQ</span>
            <Icon name="chevRight" size={18} />
          </a>

          <div className="oauth-perms">
            <div className="perms-title">
              <Icon name="shield" size={14} /> We'll request read access to:
            </div>
            <ul>
              <li><Icon name="users" size={14} /> Members &amp; contacts</li>
              <li><Icon name="calendar" size={14} /> Events &amp; registrations</li>
              <li><Icon name="tag" size={14} /> Custom fields &amp; groups</li>
            </ul>
          </div>

          <div className="login-foot">
            <Icon name="info" size={13} />
            <span>Read-only by default. You can revoke access anytime in TidyHQ → Apps.</span>
          </div>
        </div>

        <aside className="login-side">
          <div className="side-eyebrow">What you get</div>
          <ul className="side-feat">
            <li>
              <span className="feat-icon" style={{ background: "var(--brand-50)", color: "var(--brand-600)" }}><Icon name="table" size={16} /></span>
              <div>
                <b>One flexible table</b>
                <p>Every registrant, every field. Show, hide and reorder columns to match the conversation in the room.</p>
              </div>
            </li>
            <li>
              <span className="feat-icon" style={{ background: "#e3f6f3", color: "#0e7a70" }}><Icon name="filter" size={16} /></span>
              <div>
                <b>Filter &amp; save views</b>
                <p>Chase unpaid balances, isolate medical flags, split groups — and save it as a view your team can re-open.</p>
              </div>
            </li>
            <li>
              <span className="feat-icon" style={{ background: "#fff5e0", color: "#8a5a00" }}><Icon name="chart" size={16} /></span>
              <div>
                <b>Charts at a glance</b>
                <p>Sign-ups over time, gender split, payment status — built from the same data as your table.</p>
              </div>
            </li>
            <li>
              <span className="feat-icon" style={{ background: "#fde7ee", color: "#a8345a" }}><Icon name="download" size={16} /></span>
              <div>
                <b>Export anything</b>
                <p>One click to CSV — your filters and visible columns come along for the ride.</p>
              </div>
            </li>
          </ul>
          <div className="side-trust">
            <div className="trust-row">
              <Icon name="shield" size={14} /> Self-hosted
              <span className="dot-sep" />
              <Icon name="key" size={14} /> OAuth 2.0
              <span className="dot-sep" />
              <Icon name="eye" size={14} /> Read-only
            </div>
          </div>
        </aside>
      </main>

      <footer className="login-foot-bar">
        <span>TidyView · An unofficial companion to TidyHQ{instance ? ` · ${instance}` : ""}</span>
      </footer>

      <style>{`
        .login-root {
          position: fixed; inset: 0;
          display: grid;
          grid-template-rows: auto 1fr auto;
          background: linear-gradient(180deg, #f7fbfd 0%, #eef6fb 100%);
          overflow: auto;
        }
        .login-bg { position: absolute; inset: 0; pointer-events: none; overflow: hidden; }
        .login-bg .grid { position: absolute; inset: 0; }
        .blob { position: absolute; border-radius: 50%; filter: blur(60px); opacity: 0.55; }
        .blob.b1 { width: 460px; height: 460px; background: radial-gradient(circle, #a8d8ea 0%, transparent 70%); top: -180px; left: -120px; }
        .blob.b2 { width: 520px; height: 520px; background: radial-gradient(circle, #b4ebe3 0%, transparent 70%); bottom: -200px; right: -180px; }
        .blob.b3 { width: 320px; height: 320px; background: radial-gradient(circle, #ffd6c8 0%, transparent 70%); top: 30%; right: 20%; }

        .login-top {
          position: relative; z-index: 1;
          display: flex; justify-content: space-between; align-items: center;
          padding: 22px 36px;
        }
        .brand-lockup { display: flex; align-items: center; gap: 10px; }
        .brand-mark {
          font-weight: 800; font-size: 18px; letter-spacing: -0.02em; color: var(--ink-900);
          display: flex; align-items: center; gap: 8px;
        }
        .brand-mark .dot { width: 22px; height: 22px; border-radius: 7px; background: var(--brand-500); position: relative; }
        .brand-mark .dot::after { content: ""; position: absolute; right: -2px; top: -2px; width: 8px; height: 8px; border-radius: 50%; background: #ffd96b; }
        .brand-mark b { color: var(--brand-600); font-weight: 800; }

        .login-main {
          position: relative; z-index: 1;
          display: grid;
          grid-template-columns: minmax(420px, 520px) minmax(360px, 440px);
          gap: 48px;
          align-items: start;
          align-self: center;
          justify-self: center;
          padding: 24px 48px 64px;
          width: 100%;
          max-width: 1100px;
          margin: 0 auto;
        }
        @media (max-width: 920px) { .login-main { grid-template-columns: 1fr; gap: 24px; } }

        .login-card {
          background: white;
          border: 1px solid var(--border);
          border-radius: var(--r-2xl);
          padding: 36px 36px 28px;
          box-shadow: var(--shadow-lg);
        }
        .login-eyebrow {
          display: inline-flex; align-items: center; gap: 8px;
          color: var(--brand-700); font-weight: 700; font-size: 12px;
          text-transform: uppercase; letter-spacing: 0.08em;
          background: var(--brand-50); padding: 6px 12px; border-radius: var(--r-full);
        }
        .dot-pulse {
          width: 8px; height: 8px; border-radius: 50%; background: var(--brand-500);
          box-shadow: 0 0 0 0 rgba(26,143,191,0.4);
          animation: pulse 2s infinite;
        }
        @keyframes pulse { 0%,100% { box-shadow: 0 0 0 0 rgba(26,143,191,0.4);} 50%{box-shadow:0 0 0 6px rgba(26,143,191,0);}}

        .login-title { font-size: 36px; line-height: 1.1; letter-spacing: -0.025em; font-weight: 800; margin: 18px 0 14px; }
        .login-title .muted { color: var(--ink-500); font-weight: 700; }
        .login-sub { color: var(--ink-600); font-size: 14.5px; line-height: 1.55; margin: 0 0 28px; }

        .oauth-btn {
          width: 100%;
          height: 56px;
          background: linear-gradient(180deg, var(--brand-500), var(--brand-600));
          color: white;
          border-radius: var(--r-lg);
          font-size: 15px;
          font-weight: 700;
          letter-spacing: -0.01em;
          display: flex; align-items: center;
          padding: 0 18px;
          box-shadow: 0 6px 16px rgba(26,143,191,0.35), inset 0 1px 0 rgba(255,255,255,0.2);
          transition: transform 100ms, box-shadow 200ms;
          text-decoration: none;
        }
        .oauth-btn:hover { transform: translateY(-1px); box-shadow: 0 10px 24px rgba(26,143,191,0.4); text-decoration: none; }
        .oauth-btn:active { transform: translateY(0); }
        .oauth-icon {
          width: 36px; height: 36px;
          border-radius: 10px;
          background: rgba(255,255,255,0.18);
          display: grid; place-content: center;
          margin-right: 14px;
        }
        .oauth-label { flex: 1; text-align: left; }

        .oauth-perms {
          margin-top: 22px;
          padding: 14px 16px;
          background: var(--ink-50);
          border-radius: var(--r-md);
          border: 1px solid var(--border);
        }
        .perms-title {
          display: flex; align-items: center; gap: 8px;
          font-size: 12px; font-weight: 700; color: var(--ink-700);
          text-transform: uppercase; letter-spacing: 0.05em;
          margin-bottom: 10px;
        }
        .oauth-perms ul { list-style: none; margin: 0; padding: 0; display: grid; gap: 6px; }
        .oauth-perms li { display: flex; align-items: center; gap: 8px; font-size: 13px; color: var(--ink-700); }
        .oauth-perms li svg { color: var(--brand-500); }

        .login-foot {
          display: flex; align-items: center; gap: 8px;
          margin-top: 18px; padding-top: 18px;
          border-top: 1px dashed var(--border);
          font-size: 12px; color: var(--ink-500);
        }
        .login-foot svg { color: var(--ink-400); flex-shrink: 0; }

        .login-side { padding-top: 24px; }
        .side-eyebrow {
          font-size: 11px; font-weight: 800; letter-spacing: 0.12em;
          text-transform: uppercase; color: var(--ink-500);
          margin-bottom: 16px;
        }
        .side-feat { list-style: none; margin: 0; padding: 0; display: grid; gap: 18px; }
        .side-feat li { display: grid; grid-template-columns: 36px 1fr; gap: 14px; align-items: start; }
        .feat-icon {
          width: 36px; height: 36px;
          border-radius: 10px;
          display: grid; place-content: center;
        }
        .side-feat b { font-size: 14.5px; font-weight: 700; color: var(--ink-900); display: block; margin-bottom: 2px; letter-spacing: -0.01em; }
        .side-feat p { color: var(--ink-600); font-size: 13.5px; line-height: 1.5; margin: 0; }

        .side-trust {
          margin-top: 26px;
          padding: 12px 16px;
          background: white;
          border: 1px solid var(--border);
          border-radius: var(--r-md);
        }
        .trust-row {
          display: flex; align-items: center; gap: 10px; flex-wrap: wrap;
          font-size: 12px; color: var(--ink-600); font-weight: 600;
        }
        .trust-row svg { color: var(--ink-400); }
        .dot-sep { width: 3px; height: 3px; border-radius: 50%; background: var(--ink-300); }

        .login-foot-bar {
          position: relative; z-index: 1;
          display: flex; justify-content: space-between; align-items: center;
          padding: 18px 36px;
          font-size: 12px; color: var(--ink-500);
        }
      `}</style>
    </div>
  );
}
